import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { FONT_FAMILY } from '../common/sharedFunctions';

const getIcon = (type) => {
  switch (type) {
    case 'success': return <CheckCircle className="w-6 h-6 text-green-500" />;
    case 'warning': return <AlertTriangle className="w-6 h-6 text-yellow-500" />;
    case 'error': return <AlertCircle className="w-6 h-6 text-red-500" />;
    case 'info':
    default: return <Info className="w-6 h-6 text-blue-500" />;
  }
};

const getIconBg = (type) => {
  switch (type) {
    case 'success': return 'bg-green-100';
    case 'warning': return 'bg-yellow-100';
    case 'error': return 'bg-red-100';
    case 'info':
    default: return 'bg-blue-100';
  }
};

export default function AlertDialog(props) {
  const { t } = useTranslation();
  const { open, onClose, children, type = 'info' } = props;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${getIconBg(type)} flex items-center justify-center`}>
                  {getIcon(type)}
                </div>
                <h3 className="text-lg font-semibold text-gray-800" style={{ fontFamily: FONT_FAMILY }}>
                  {t('alert') || 'Alert'}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-5">
              <p className="text-gray-600" style={{ fontFamily: FONT_FAMILY }}>
                {children}
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end">
              <button
                onClick={onClose}
                className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-medium transition-all shadow-md hover:shadow-lg"
                style={{ fontFamily: FONT_FAMILY }}
              >
                {t('close')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
